<template>
  <div
    class="mx-table"
    :class="{
      'mx-table1': isNewsty,
      'mx-table-with-bottom-scrollbar': bottomScrollbar,
    }"
  >
    <Spin size="large" fix v-if="isConfiguring"></Spin>
    <div class="tabBtn">
      <div>
        <slot name="button"></slot>
      </div>
    </div>
    <Table
      ref="table"
      :key="tableKey"
      v-bind="$props"
      v-on="$listeners"
      :columns="columnsHeader"
      :height="height || tableHeight"
      :loading="loading"
      :draggable="draggable"
      no-data-text="暂无数据"
      no-filtered-data-text="暂无数据"
      :row-class-name="isWarn && isWarn == true ? rowClassName : rowClassNames"
    ></Table>
    <div
      v-show="bottomScrollbar && showBottomScrollbar"
      ref="bottomScrollbar"
      class="mx-table-bottom-scrollbar"
      @scroll="syncTableScroll"
    >
      <div :style="{ width: `${bottomScrollbarWidth}px` }"></div>
    </div>
    <div class="pages">
      <Page
        :total="total"
        :current="current"
        :page-size="pageSize"
        :page-size-opts="[10, 20, 30, 40]"
        placement="top"
        show-sizer
        :transfer="true"
        :show-total="showTotal"
        @on-change="pageChange"
        @on-page-size-change="sizeChange"
      ></Page>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    columns: {},
    data: Array,
    total: Number,
    loading: Boolean,
    current: Number,
    pageSize: Number,
    height: [Number, String],
    'no-data-text': String,
    stripe: Boolean,
    isNewsty: Boolean,
    isWarn: Boolean,
    draggable: Boolean,
    bottomScrollbar: Boolean,
    showTotal: {
      type: Boolean,
      default: true,
    },
    rowClassName: Function,
  },
  data() {
    return {
      tableHeight: 0,
      columnsHeader: [],
      tableKey: 0,
      isConfiguring: false,
      bottomScrollbarWidth: 0,
      showBottomScrollbar: false,
      dataRefreshFrame: null,
    }
  },
  mounted() {
    if (this.$refs.table && this.$refs.table.$el) {
      this.tableHeight = this.$refs.table.$el.offsetHeight
    }
    window.addEventListener('resize', this.flexHeight)
    this.$eventBus.on('mx-column-setting-saved', this.initColumnsHeader)
    this.initColumnsHeader()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.flexHeight)
    this.$eventBus.off('mx-column-setting-saved', this.initColumnsHeader)
    if (this.dataRefreshFrame) {
      cancelAnimationFrame(this.dataRefreshFrame)
      this.dataRefreshFrame = null
    }
  },
  watch: {
    columns: {
      handler() {
        this.initColumnsHeader()
      },
      deep: true,
    },
    data() {
      this.$nextTick(this.resetScrollAndLayout)
    },
    columnsHeader: {
      handler() {
        this.$nextTick(this.updateBottomScrollbar)
      },
      deep: true,
    },
  },
  methods: {
    initColumnsHeader(payload = {}) {
      if (payload && payload.path && payload.path !== this.$route.path) return
      const columnsList = Array.isArray(this.columns)
        ? Array.from(this.columns)
        : []
      const path = this.$route.path
      let data = null
      const localStorageColumn = localStorage.getItem('columnJson')
      if (localStorageColumn) {
        try {
          const parsed = JSON.parse(localStorageColumn)
          data = parsed[path]
        } catch (error) {
          data = null
        }
      }
      let list = []
      if (data && data.length > 0) {
        const columnMap = new Map()
        for (let i = 0; i < columnsList.length; i++) {
          const col = columnsList[i]
          if (col.title) columnMap.set(col.title, col)
        }

        const hasChecked = data.some((item) => item.check)
        for (let i = 0; i < data.length; i++) {
          const item = data[i]
          const col = columnMap.get(item.title)
          if (col && (!hasChecked || item.fixed || item.check)) {
            list.push(col)
          }
        }
      } else {
        list = columnsList
      }
      const newColumnsHeader = this.normalizeColumnsHeader(
        columnsList,
        data,
        list
      )

      const getColKey = (column) =>
        `${
          column.key || column.colKey || column.type || column.title || ''
        }-${column.width || ''}-${column.fixed || ''}`
      const oldKeys = this.columnsHeader.map(getColKey).join('|')
      const newKeys = newColumnsHeader.map(getColKey).join('|')

      if (oldKeys !== newKeys) {
        this.isConfiguring = true
        setTimeout(() => {
          this.columnsHeader = newColumnsHeader
          this.tableKey += 1
          this.$nextTick(() => {
            setTimeout(() => {
              this.isConfiguring = false
            }, 500)
          })
        }, 1000)
      } else {
        this.columnsHeader = newColumnsHeader
      }
    },
    normalizeColumnsHeader(columnsList, savedColumns, resolvedColumns) {
      if (!Array.isArray(savedColumns) || savedColumns.length === 0) {
        return resolvedColumns
      }
      const hasChecked = savedColumns.some((item) => item.check)
      if (!hasChecked) return resolvedColumns

      const hasNormalColumns = columnsList.some((item) => !item.fixed)
      const resolvedNormalCount = resolvedColumns.filter(
        (item) => !item.fixed
      ).length
      if (hasNormalColumns && resolvedNormalCount === 0) {
        return columnsList
      }
      return resolvedColumns.length ? resolvedColumns : columnsList
    },
    pageChange(pageChanged) {
      this.$emit('pageChange', pageChanged)
    },
    sizeChange(sizeChanged) {
      this.$emit('sizeChange', sizeChanged)
    },
    selectAll(status) {
      this.$refs.table.selectAll(status)
    },
    rowClassNames() {
      return ''
    },
    // View Design 的主表、左固定列和右固定列是三个独立滚动容器。
    // 数据替换后统一回到顶部并重算布局，避免刷新前的 scrollTop 使固定列与主表错行。
    resetScrollAndLayout() {
      if (this.dataRefreshFrame) cancelAnimationFrame(this.dataRefreshFrame)
      this.dataRefreshFrame = requestAnimationFrame(() => {
        this.dataRefreshFrame = null
        const table = this.$refs.table
        if (!table) return

        const tableRefs = table.$refs || {}
        const scrollRefNames = ['body', 'fixedBody', 'fixedRightBody']
        scrollRefNames.forEach((refName) => {
          const scrollContainer = tableRefs[refName]
          if (scrollContainer) scrollContainer.scrollTop = 0
        })

        if (typeof table.handleResize === 'function') table.handleResize()
        this.flexHeight()
      })
    },
    flexHeight() {
      this.tableHeight = 0
      this.$nextTick(() => {
        if (this.$refs.table && this.$refs.table.$el) {
          this.tableHeight = this.$refs.table.$el.offsetHeight
        }
        this.updateBottomScrollbar()
      })
    },
    updateBottomScrollbar() {
      if (!this.bottomScrollbar || !this.$refs.table) return
      const body = this.$refs.table.$refs.body
      if (!body) {
        this.showBottomScrollbar = false
        return
      }
      this.bottomScrollbarWidth = body.scrollWidth
      const shouldShow = body.scrollWidth > body.clientWidth
      if (this.showBottomScrollbar !== shouldShow) {
        this.showBottomScrollbar = shouldShow
        this.tableHeight = 0
        this.$nextTick(() => {
          if (this.$refs.table && this.$refs.table.$el) {
            this.tableHeight = this.$refs.table.$el.offsetHeight
          }
        })
      }
      if (this.$refs.bottomScrollbar) {
        this.$refs.bottomScrollbar.scrollLeft = body.scrollLeft
      }
    },
    syncTableScroll(event) {
      const body = this.$refs.table && this.$refs.table.$refs.body
      if (body) body.scrollLeft = event.target.scrollLeft
    },
  },
}
</script>

<style lang="less" scoped>
.mx-table {
  width: 100%;
  display: flex;
  flex-flow: column;
  flex: auto;
  min-height: 0;
  background: #fff;
  padding: 8px 20px 20px;
  border-radius: 0 0 6px 6px;
  box-sizing: border-box;
  position: relative;

  /deep/.ivu-table-wrapper {
    height: 100%;
    flex: 1;
    min-height: 0;

    .ivu-table:before {
      display: none;
    }
  }

  /deep/.ivu-table-body {
    overflow-y: auto !important;
  }

  &.mx-table-with-bottom-scrollbar /deep/.ivu-table-body {
    overflow-x: hidden !important;
  }

  /deep/ .ivu-table th {
    height: 48px;
    background-color: #f2f3f5;
    font-weight: 400;
    color: #272e3b;
  }

  .pages {
    margin-top: 15px;
    text-align: right;
  }

  /deep/.ivu-page-total {
    float: left;
  }

  /deep/.ivu-page-item {
    min-width: 32px;
    height: 32px;
    line-height: 30px;
  }

  /deep/.ivu-page-item-active {
    border-color: rgba(20, 86, 240, 1);
    background-color: rgba(20, 86, 240, 1);

    a {
      color: #fff;
    }
  }

  /deep/.ivu-page-prev,
  /deep/.ivu-page-next,
  /deep/.ivu-page-item-jump-prev,
  /deep/.ivu-page-item-jump-next {
    min-width: 32px;
    height: 32px;
    line-height: 30px;
  }

  /deep/.ivu-page-options {
    .ivu-select-selection {
      height: 32px;
    }

    .ivu-select-selected-value {
      min-width: 32px;
      height: 32px;
      line-height: 30px;
    }
  }
}

.mx-table-bottom-scrollbar {
  width: 100%;
  height: 17px;
  overflow-x: scroll;
  overflow-y: hidden;
  flex: none;

  > div {
    height: 1px;
  }
}

.tabBtn {
  display: flex;
  justify-content: space-between;

  .settingCode {
    margin-left: 6px;

    /deep/span {
      display: flex !important;
      align-items: center !important;
      gap: 6px;
    }
  }

  > div {
    flex: 1;
  }
}

.mx-table1 {
  padding: 0 24px 16px;
}
</style>
